import classNames from 'classnames';
import ENDPOINT from 'constants/endpoints'
import useApi from 'hooks/useApi'
import moment from 'moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import BasicButton from '../../../../../components/BasicButton';
import Card from '../../../../../components/Card';
import FormContentLoader from '../../../../../components/FormContentLoader'
import PaymentMethods from '../../../../../components/PaymentMethods';
import ConfirmDialog from '../../../../Dialog/components/ConfirmDialog';
import ResponseErrorDialog from '../../../../Dialog/components/ResponseErrorDialog'
import './SubscriptionSection.css'

const SubscriptionSection = () => {
  const api = useApi();
  const [info, setInfo] = useState({
    loading: true,
    features: [],
    product: null,
    subscription: null,
    customer: null,
    paymentMethods: [],
  });

  const currentPlanSubtitle = useMemo(() => {
    if (!info.subscription) {
      return '';
    }

    const currentPeriodEnd = moment(info.subscription.current_period_end * 1000);

    if (info.subscription.cancel_at_period_end) {
      return `Your plan will be canceled on ${currentPeriodEnd.format('MMMM D, YYYY')}`;
    } else {
      return `Your plan renews on ${currentPeriodEnd.format('MMMM D, YYYY')}`;
    }
  }, [info.subscription]);

  const resumePlan = useCallback(() => {
    const currentPeriodEnd = moment(info.subscription.current_period_end * 1000);
    ConfirmDialog({
      title: 'Renew your plan',
      dismissWhenClose: true,
      bodyRender: (
        <div>
          <p>
            This plan will no longer be canceled. It will renew on {currentPeriodEnd.format('MMMM D, YYYY')}.
          </p>
        </div>
      ),
      textSuccessBtn: 'Renew Plan',
      textCancelBtn: 'Back',
      className: 'popup-recovered',
      popupStyles: {
        maxWidth: 520,
      }
    }).then(function () {
      setInfo({...info, loading: true});

      api.postRequest(
        ENDPOINT.ACTION.ACCOUNT.RESUME_PLAN,
        {},
        {
          handleError: response => ResponseErrorDialog({response}),
          handleSuccess: () => {
            getSubscriptionInfo();
          },
        }
      );
    });
  }, [info]);

  const cancelPlan = useCallback(() => {
    const currentPeriodEnd = moment(info.subscription.current_period_end * 1000);
    ConfirmDialog({
      title: 'Cancel your plan',
      dismissWhenClose: true,
      bodyRender: (
        <div>
          <p>
            Your plan will be canceled, but is still available until the end of your billing period on {currentPeriodEnd.format('MMMM D, YYYY')}.
          </p>
          <p>
            If you change your mind, you can renew your subscription.
          </p>
        </div>
      ),
      textSuccessBtn: 'Cancel Plan',
      textCancelBtn: 'Back',
      className: 'popup-recovered',
      popupStyles: {
        maxWidth: 520,
      }
    }).then(function () {
      setInfo({...info, loading: true});

      api.postRequest(
        ENDPOINT.ACTION.ACCOUNT.CANCEL_PLAN,
        {},
        {
          handleError: response => ResponseErrorDialog({ response }),
          handleSuccess: () => {
            getSubscriptionInfo();
          },
        }
      );
    });
  }, [info]);

  const actions = useMemo(() => {
    if (info.loading || !info.subscription || (info.subscription && info.subscription.status !== 'active')) {
      return [];
    }

    if (info.subscription.id === 'paypal') {
      return [];
    }

    if (info.subscription.cancel_at_period_end) {
      return [
        { label: 'Resume Plan', onClick: resumePlan },
      ];
    } else if (info.subscription) {
      return [
        { label: 'Cancel Plan', onClick: cancelPlan },
      ];
    }
  }, [info]);

  const currentPaymentMethod = useMemo(() => {
    if (!info.paymentMethods || info.paymentMethods.length === 0 || !info.subscription) {
      return null;
    }

    return info.paymentMethods.find(({ id }) => {
      const defId = info.subscription.default_payment_method || info.customer.invoice_settings.default_payment_method;

      return id === defId;
    });
  }, [info.subscription, info.paymentMethods]);

  const productPrice = useMemo(() => {
    if (!info.product || !info.product.price) {
      return '';
    }

    const price = info.product.price.unit_amount / 100;
    const period = info.product.price.recurring ? info.product.price.recurring.interval : '';

    return `$${price} per ${period}`;
  }, [info.product]);

  const getSubscriptionInfo = useCallback(() => {
    api.getRequest(
      ENDPOINT.FORM.GET.ACCOUNT_PLAN,
      {},
      {
        handleError: response => ResponseErrorDialog({ response }),
        handleSuccess: response => {
          setInfo({ ...info, ...response, loading: false });
        },
      }
    );
  }, []);

  useEffect(() => {
    getSubscriptionInfo();
  }, []);

  const updateSubscription = useCallback((selected) => {
    setInfo({...info, loading: true});

    if (selected === 'new') {
      api.postRequest(
        ENDPOINT.ACTION.ACCOUNT.CREATE_PAYMENT_METHOD,
        {},
        {
          handleError: response => {
            ResponseErrorDialog({response});
            setInfo({...info, loading: false});
          },
          handleSuccess: response => {
            setInfo({...info, loading: false});
            window.location.href = response.url;
          },
        }
      );
      //
      return;
    }

    const data = {
      paymentMethodId: selected,
    };
    api.postRequest(
      ENDPOINT.ACTION.ACCOUNT.UPDATE_SUBSCRIPTION,
      data,
      {
        handleError: response => {
          ResponseErrorDialog({response});
          setInfo({...info, loading: false});
        },
        handleSuccess: response => {
          setInfo({ ...info, subscription: response.subscription, loading: false });
        },
      }
    );
  }, [info]);

  return (
    <>
      <div className={'form__content form__content--full-width'} style={{marginBottom: 16}}>
        <div className="form__header"><a name="subscription"></a>
          <h4 className="form__title">GoBuyside Account</h4>
        </div>

        <div className={'form__group subscription-section'}>
          <Card title={'Current plan'} loading={info.loading} actions={actions}>
            {info.product && (
              <div className={'current-plan'}>
                <div className={classNames('current-plan__title', {'is--highlighted': info.product.id !== 'free'})}>{info.product.name}</div>
                {info.product.id === 'free' && (
                  <div className={'current-plan__free'}>
                    <p>Upgrade to get access to premium account</p>
                    <BasicButton btnArrow={false} btnHref={'/candidate/premium'} btnText={'Change plan'} />
                  </div>
                )}
                {productPrice && (
                  <div className="current-plan__price">{productPrice}</div>
                )}
                {currentPlanSubtitle && (
                  <div className={'current-plan__subtitle'}>{currentPlanSubtitle}</div>
                )}

                {currentPaymentMethod && (
                  <PaymentMethods
                    data={[currentPaymentMethod]}
                    defaultId={info.subscription ? info.subscription.default_payment_method : undefined}
                    extended={false}
                    editable={true}
                    onEdit={updateSubscription}
                  />
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
      <div className={'form__content form__content--full-width'}>
        <div className="form__header" style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16}}>
          <h4 className="form__title">Your Plan Features</h4>
          <BasicButton btnArrow={false} btnHref={'/candidate/premium'} btnText={'Change Plan'} />
        </div>

        <div className={'form__group subscription-section'}>
          {info.loading ? (
            <FormContentLoader />
          ) : (
            <ul className={'plan-features'}>
              <li className={'plan-features__list'}>
                {info.features.map((feature, index) => {
                  const hasFeature = info.product && info.product.features.includes(feature);
                  const [title, description] = feature.split(':');

                  return (
                    <div key={index} className={'plan-features__item'}>
                      <span className={'plan-features__content'}>
                        <span className={'plan-features__label'}>{title}</span>
                        <span className={'plan-features__description'}>{description}</span>
                      </span>
                      <span className={'plan-features__icon'}>
                        {hasFeature && (
                          <svg width="20" height="20" viewBox={'0 0 20 20'} fill="none">
                            <path stroke="#27282D" strokeWidth="4" d="M2 10.5 7.5 16 18 5.5"/>
                          </svg>
                        )}
                        {!hasFeature && (
                          <svg width="20" height="20" viewBox={'0 0 20 20'} fill="none">
                            <path stroke="#EB5428" strokeWidth="4" d="M15.9 5.09 3.88 17.1M4.41 5.09l12.02 12.02"/>
                          </svg>
                        )}
                      </span>
                    </div>
                  );

                })}
              </li>
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

export default SubscriptionSection;
